/* eslint-disable custom/no-restricted-translation-jsx-untranslated-children-text */
import React from 'react';

import { List, ListItem, ListItemText, Typography } from '@mui/material';

import { useIntl } from '@kamernet/core/Intl';
import { Locale } from '@kamernet/core/Intl/types';

import styles from './Content.module.css';
import { SignUpButton } from './SignUpButton';

interface ContentCityStudioProps {
  cityName: string;
}

export const ContentCityStudio = ({ cityName }: ContentCityStudioProps) => {
  const { locale } = useIntl();

  return locale === Locale.en ? (
    <React.Fragment>
      <Typography variant="h1" className={styles.title}>
        {`Studio for rent in ${cityName}, Netherlands`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Looking to rent a studio in ${cityName}, Use Kamernet and rent studios in
          ${cityName} from top letting agents and private homeowners. Our site has
          the largest selection of studio apartments for rent in ${cityName} where
          you can choose from a wide variety of studio flats in ${cityName} available
          on both long-term and short-term basis. Kamernet is the best website to
          find studio apartments in ${cityName} having thousands of listings that are
          updated regularly. Our platform allows you to choose from a wide
          variety of studios for rent in ${cityName} online and schedule a viewing
          instantly. Browse the best studio apartments in ${cityName} and view their
          updated prices, availability and photos to make your decision.
          `}
      </Typography>
      <Typography>
        {`There are various types of studios for rent across ${cityName}, but you may need
          to decide on what type of studio you wish to live in. Here's what you
          need to know about the different types of studios for rent across
          ${cityName} that you can expect to find on Kamernet.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Affordable and Cheap Studios for Rent
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Finding cheap studio apartments in ${cityName} can be difficult, but on our
          website you can find cheap studios for rent in ${cityName} at the lowest
          prices. Kamernet is the most trusted rental site that lets you discover
          affordable studio apartments so you can book cheap studios in ${cityName}
          that are perfect fit for your lifestyle and budget.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Furnished and Unfurnished Rooms
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`If you are looking for a furnished studio in ${cityName}, then Kamernet has
          the largest selection of fully furnished studio apartments in ${cityName}
          that are fully equipped and ready to move. Tenants can also find semi
          furnished studios for rent in ${cityName} with basic furnishing for more
          personalization and convenient living experience. Since the rent of an
          unfurnished studio is lower than a furnished one, you can also search
          for an unfurnished studio for rent in ${cityName} to stay on a budget.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Short term and Long term Studios
      </Typography>
      <Typography>
        {`If you’re looking for long term studio rentals in ${cityName}, then Kamernet
          will help you find a studio to rent in ${cityName} for long term for your
          extended stay. For tenants searching for short term studio rentals in
          ${cityName}, our platform can help you find a studio for rent in ${cityName} for
          short term.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Luxury studio apartments
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Looking for luxury studio apartments in ${cityName}? Kamernet offers fully
          furnished studios in ${cityName} and near prime locations. On our platform,
          you can expect to find luxury studios for rent in ${cityName} for the
          perfect high-end living experience. Live in style with luxury studio
          apartments in ${cityName} that are equipped with all the amenities needed
          during your stay.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Studio Apartments for International Students
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`If you are searching for studios in ${cityName} for students, then we have
          a wide selection of student studios in ${cityName} that you can book easily
          and quickly. Since affordable student studio apartments in ${cityName} and
          the surrounding area are limited, our platform provides the largest
          selection of student studio flats in ${cityName} at affordable prices.`}
      </Typography>
      <Typography
        variant="h4"
        className={styles.title}
      >{`How to find studios in ${cityName} with Kamernet?`}</Typography>
      <List component="ol" className={styles.list}>
        <SignUpButton />
        <ListItem className={styles.listItem}>
          <ListItemText primary="Get Kamernet premium account to respond to unlimited adverts" />
        </ListItem>
        <ListItem className={styles.listItem}>
          <ListItemText primary="Choose the plan that's right for you 15-Days, 30-Days, 3-Months" />
        </ListItem>
        <ListItem className={styles.listItem}>
          <ListItemText primary="Enter a payment method to start subscription and respond to adverts." />
        </ListItem>
      </List>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <Typography variant="h1" className={styles.title}>
        {`Studio te huur in ${cityName}, Nederland`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Op zoek naar een studio te huur in ${cityName}, Gebruik Kamernet en huur
          studio's in ${cityName} van top verhuurmakelaars en particuliere
          verhuurders. Onze site heeft het grootste aanbod van studio's te huur
          in ${cityName} waar je kunt kiezen uit een grote selectie van studio's in
          ${cityName} beschikbaar voor zowel lange als korte termijn. Kamernet is de
          beste website om studio appartementen te vinden in ${cityName} met
          duizenden advertenties die regelmatig worden bijgewerkt. Via ons
          platform kun je online kiezen uit een grote selectie studio's te huur
          in ${cityName} en direct een bezichtiging plannen. Zoek de beste
          studio-appartementen in ${cityName} en bekijk de bijgewerkte prijzen,
          beschikbaarheid en foto's om je beslissing te maken.`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Er zijn verschillende soorten studio's te huur in ${cityName}, maar het is aan jou
          om te beslissen in welk type studio je wilt wonen. Hier is wat je wilt
          weten over de verschillende soorten studio's te huur in ${cityName} die je
          kunt vinden op Kamernet.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Betaalbare en goedkope studio&apos;s te huur
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Het vinden van goedkope studio-appartementen in ${cityName} kan moeilijk
          zijn, maar op onze website vind je goedkope studio's te huur in ${cityName}
          tegen de laagste prijzen. Kamernet is de meest betrouwbare verhuursite
          die je betaalbare studio-appartementen laat ontdekken zodat je
          goedkope studio's in ${cityName} kunt huren die perfect passen bij jouw
          levensstijl en budget.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Gemeubileerde en ongemeubileerde kamers
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Als je op zoek bent naar een gemeubileerde studio in ${cityName}, dan heeft
          Kamernet het grootste aanbod aan volledig gemeubileerde
          studio-appartementen in ${cityName} die volledig zijn ingericht en klaar
          zijn om te verhuizen. Huurders kunnen ook gedeeltelijk gemeubileerde
          studio's te huur vinden in ${cityName} met een basisinrichting voor meer
          personalisatie en een comfortabele woonervaring. Aangezien de huur van
          een ongemeubileerde studio lager is dan die van een gemeubileerde
          studio, kun je ook een ongemeubileerde studio te huur zoeken in ${cityName}
          om binnen je budget te blijven.`}
      </Typography>
      <Typography variant="h3">
        Studio&apos;s voor korte en lange termijn
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Als je op zoek bent naar een studio huur voor langere termijn in
          ${cityName}, dan helpt Kamernet je bij het vinden van een studio die je voor
          langere tijd in ${cityName} kunt huren voor je langere verblijf. Voor
          huurders die op zoek zijn naar studio verhuur voor korte termijn in
          ${cityName}, kan ons platform-je ook helpen bij het vinden van een studio
          te huur in ${cityName} voor korte termijn.`}
      </Typography>
      <Typography variant="h3">Luxe studio-appartementen</Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Op zoek naar luxe studio appartementen in ${cityName}? Kamernet biedt
          volledig ingerichte studio's in ${cityName} en nabij toplocaties. Op ons
          platform kun je luxe studio's verwachten die te huur zijn in ${cityName}
          voor de perfecte high-end woonervaring. Leef in stijl met luxe
          studio-appartementen in ${cityName} die zijn uitgerust met alle
          voorzieningen die je nodig hebt tijdens je verblijf.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Studio-appartementen voor internationale studenten
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Als je op zoek bent naar studio's in ${cityName} voor studenten, dan hebben
          wij een ruime keuze aan studentenstudio's in ${cityName} die je gemakkelijk
          en snel kunt huren. Omdat betaalbare studentenstudio's in ${cityName} en
          omgeving beperkt zijn, biedt ons platform de grootste selectie
          studentenstudio's in ${cityName} tegen betaalbare prijzen.`}
      </Typography>
      <Typography variant="h4" className={styles.title}>
        {`Hoe vind je studio's in ${cityName} met Kamernet?`}
      </Typography>
      <List component="ol" className={styles.list}>
        <SignUpButton />
        <ListItem className={styles.listItem}>
          <ListItemText
            primary={`Neem een Kamernet premium account om onbeperkt te reageren op
              advertenties`}
          />
        </ListItem>
        <ListItem className={styles.listItem}>
          <ListItemText primary="Kies het plan dat bij je past 15-dagen, 30-dagen, 3 maanden" />
        </ListItem>
        <ListItem className={styles.listItem}>
          <ListItemText
            primary="Voer een betaalmethode in om het abonnement te starten en op
              advertenties te reageren."
          />
        </ListItem>
      </List>
    </React.Fragment>
  );
};
